import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Check, X, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPanel({ user }) {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== 'poc') {
      toast.error('Access denied: POC only');
      navigate('/dashboard');
      return;
    }
    fetchRequests();
  }, [user, navigate]);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/admin/requests`, {
        withCredentials: true
      });
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Failed to load edit requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    try {
      await axios.put(`${API}/admin/requests/${requestId}/approve`, {}, {
        withCredentials: true
      });
      toast.success('Request approved');
      fetchRequests();
    } catch (error) {
      toast.error('Failed to approve request');
    }
  };

  const handleReject = async (requestId) => {
    try {
      await axios.put(`${API}/admin/requests/${requestId}/reject`, {}, {
        withCredentials: true
      });
      toast.success('Request rejected');
      fetchRequests();
    } catch (error) {
      toast.error('Failed to reject request');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-6 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          data-testid="back-btn"
          className="mb-6 text-foreground-muted hover:text-primary"
        >
          <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
          Back to Dashboard
        </Button>

        <div className="mb-8">
          <h1 className="text-4xl font-serif tracking-tight text-foreground mb-2">Admin Panel</h1>
          <p className="text-base font-sans text-foreground-muted">
            Review and manage pending edit requests
          </p>
        </div>

        {requests.length === 0 ? (
          <div className="bg-white border border-border shadow-card rounded-lg p-12 text-center">
            <Clock className="w-16 h-16 text-primary mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="text-2xl font-serif text-foreground mb-2">No Pending Requests</h3>
            <p className="text-base font-sans text-foreground-muted">
              All edit requests have been processed
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map((request, index) => (
              <div 
                key={request.request_id} 
                className="bg-white border border-border shadow-card rounded-lg p-6"
                data-testid={`edit-request-${index}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-serif text-foreground mb-1" data-testid="request-action">
                      {request.action === 'create' ? 'Add New Member' : 'Update Member'}
                    </h3>
                    <p className="text-sm font-sans text-foreground-muted">
                      Requested by {request.user_name}
                    </p>
                  </div>
                  <span className="text-xs font-mono text-muted-foreground uppercase tracking-widest px-3 py-1 bg-accent rounded-full">
                    {request.status}
                  </span>
                </div>

                {/* Changes preview */}
                <div className="bg-paper p-4 rounded-lg mb-4">
                  <h4 className="text-sm font-mono text-muted-foreground uppercase tracking-widest mb-2">Changes</h4>
                  <pre className="text-sm font-mono text-foreground whitespace-pre-wrap" data-testid="request-changes">
                    {JSON.stringify(request.changes, null, 2)}
                  </pre>
                </div>

                {/* Actions */}
                <div className="flex space-x-3">
                  <Button
                    onClick={() => handleApprove(request.request_id)}
                    data-testid="approve-btn"
                    className="bg-status-success text-white hover:opacity-90 rounded-full px-6 py-2 font-serif tracking-wide"
                  >
                    <Check className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Approve
                  </Button>
                  <Button
                    onClick={() => handleReject(request.request_id)}
                    data-testid="reject-btn"
                    className="bg-status-error text-white hover:opacity-90 rounded-full px-6 py-2 font-serif tracking-wide"
                  >
                    <X className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}